import React from 'react';

import {Anchor, Icon, WithTooltip} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';
import {renderMoment} from 'lib/timeUtils';

const UNARCHIVE_HELP_ARTICLE_URL =
  'https://help.albert.io/en/articles/1885019-how-do-i-archive-or-unarchive-a-class';

const TOOLTIP_PROPS = {
  placement: 'right',
  theme: 'light',
  tooltipClassName: 'rounded-x1! shadow-l2!'
} as const;

/** Mirrors AutoArchiveClassroomsBanner's warning window: a future date within two weeks. */
export const isAutoArchivingSoon = (classroom: ClassroomModelV1 | null): boolean => {
  const autoArchiveDate = classroom?.getAutoArchiveDate?.();
  if (!autoArchiveDate) {
    return false;
  }
  return (
    systemTimeOffsetStore.getCurrentTime().isBefore(autoArchiveDate) &&
    systemTimeOffsetStore.getCurrentTime().add(2, 'weeks').isAfter(autoArchiveDate)
  );
};

export const AutoArchiveChip = ({classroom}: {classroom: ClassroomModelV1}) => {
  const archiveDate = renderMoment(classroom.getAutoArchiveDate(), 'dateFormalShortWithFullMonth');

  return (
    <WithTooltip
      {...TOOLTIP_PROPS}
      content={
        <>
          On {archiveDate}, Albert will automatically archive some of your classes. Archiving a
          classroom makes it unavailable to students, and prevents additional enrollments. You can
          still view data from this class, but you will not be able to create new assignments.
          Unarchive your class at any time by following the directions in this{' '}
          <Anchor
            as='a'
            href={UNARCHIVE_HELP_ARTICLE_URL}
            inheritColor
            rel='noopener noreferrer'
            target='_blank'
            underlined
          >
            help article
          </Anchor>
          .
        </>
      }
    >
      {/* Raw span: no warning Chip variant exists yet */}
      <span className='inline-flex items-center gap-x0-half rounded-full border border-[color:var(--color-content-warning)] background-primary px-x1 py-x0-quarter text-s content-warning'>
        Auto-archiving on {archiveDate}
        <Icon icon='info-circle' iconStyle='regular' />
      </span>
    </WithTooltip>
  );
};
